/**
 * Dashboard percakapan customer — SATU kolom waktu, bukan aplikasi chat.
 *
 * Owner 31 Agustus 2026: *"dashboard sendiri yg menampilkan percakapan tiap
 * customer, utamanya yg sudah melakukan pembayaran tanda jadi agar semua
 * kelihatan historynya."*
 *
 * ---------------------------------------------------------------------------
 * ⛔ YANG SENGAJA TIDAK DIBANGUN: KOTAK BALAS
 * ---------------------------------------------------------------------------
 *
 * Membalas customer tetap di Cekat, dan itu keputusan — bukan kekurangan.
 * Inbox tandingan mewarisi seluruh pekerjaan berat chat untuk mengalahkan alat
 * yang sudah menang. Yang Cekat NOL BISA tunjukkan adalah konteks pesanannya,
 * dan itu satu-satunya yang layar ini tambahkan.
 *
 * ---------------------------------------------------------------------------
 * ⛔ TIGA KEADAAN YANG WAJIB DIBEDAKAN, DAN KETIGANYA TERLIHAT MIRIP
 * ---------------------------------------------------------------------------
 *
 *   nomor nol terbaca   -> customer NOL AKAN PERNAH menerima pesan apa pun.
 *                          Yang perlu diperbaiki NOMORNYA.
 *   nomor terbaca, nol
 *   ada yang cocok      -> ia memang belum pernah chat. Nol ada yang rusak.
 *   ada percakapan      -> tampilkan.
 *
 * Layar yang menyatukan ketiganya jadi "belum ada percakapan" membuat nomor
 * yang salah ketik nol pernah ketahuan — sampai ada yang bertanya kenapa
 * customer itu nol pernah membalas apa pun.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { formatDateTime } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import Hint from "@/components/etams/Hint";
import PanelEskalasiWa from "@/components/etams/PanelEskalasiWa";

// Kode status pesan keluar → kata yang dibaca CS (K200 butir 6).
// DIKIRIM_MANUAL (K207 W6, F1): staf mengirimnya dari nomor lain saat layanan WhatsApp gangguan.
const KATA_STATUS_PESAN = { PENDING: "Belum terkirim", DIBATALKAN: "Dibatalkan", GAGAL: "Gagal terkirim", DIKIRIM_MANUAL: "Dikirim manual" };

const NADA = {
  MASUK: "border-l-info",
  KELUAR: "border-l-success",
  SISTEM: "border-l-neutral",
};

function Baris({ b, isOwner }) {
  const isi = b.peristiwa
    || b.muatan?.tautan
    || b.muatan?.message
    || b.muatan?.text
    || b.muatan?.pesan
    || "—";
  return (
    <div className={`border-l-2 ${NADA[b.arah]} pl-3 py-2`}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">
          {b.arah === "MASUK" ? "Customer" : b.arah === "KELUAR" ? "ETAMS" : "Sistem"}
        </span>
        <span>{formatDateTime(b.waktu)}</span>
        {isOwner && b.jenis && <span className="rounded border border-border px-1.5">{b.jenis}</span>}
        {/* ⚠ Status pesan keluar DITAMPILKAN. Pesan yang gagal terkirim
            terlihat persis sama dengan yang sampai kalau statusnya
            disembunyikan — dan yang membaca layar ini sedang mengira
            customer sudah diberi tahu. */}
        {b.status && b.status !== "SENT" && (
          <span className="rounded border border-warning-soft-border bg-warning-soft px-1.5 text-warning-soft-foreground">
            {KATA_STATUS_PESAN[b.status] || "Belum terkirim"}
          </span>
        )}
      </div>
      <p className="mt-0.5 break-words text-sm text-foreground">{isi}</p>
      {b.galat && (
        <p className="mt-0.5 text-xs text-destructive">{isOwner ? b.galat : "Pesan tidak terkirim."}</p>
      )}
    </div>
  );
}

export default function Percakapan() {
  const { profile } = useAuth();
  const isOwner = profile?.role?.code === "OWNER";
  const [daftar, setDaftar] = useState([]);
  const [pilih, setPilih] = useState(null);
  const [tl, setTl] = useState(null);
  const [muat, setMuat] = useState(true);
  const [galat, setGalat] = useState("");

  const muatDaftar = useCallback(async () => {
    setMuat(true);
    setGalat("");
    try {
      const { data } = await api.get("/percakapan/sudah-bayar");
      setDaftar(data.items || []);
    } catch (e) {
      setGalat(apiErrorMessage(e));
    } finally {
      setMuat(false);
    }
  }, []);

  useEffect(() => { muatDaftar(); }, [muatDaftar]);

  const buka = async (c) => {
    setPilih(c.customer_id);
    setTl(null);
    setGalat("");
    try {
      const { data } = await api.get(`/percakapan/${c.customer_id}`);
      setTl(data);
    } catch (e) {
      setGalat(apiErrorMessage(e));
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">
          Percakapan{" "}
          <Hint>Pesan WhatsApp dan peristiwa pesanan dalam satu kolom waktu. Membalas tetap lewat Cekat.</Hint>
        </h1>
      </div>

      {/* K207 W5: customer yang diteruskan AI ke CS — ditandai di sini, dibalas di Cekat. */}
      <PanelEskalasiWa />

      {galat && (
        <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {galat}
        </div>
      )}

      {muat && <p className="text-sm text-muted-foreground">Memuat…</p>}

      {!muat && daftar.length === 0 && (
        <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Belum ada customer yang tanda jadinya sudah dikonfirmasi di wilayah Anda.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[20rem_1fr]">
        <div className="space-y-2">
          {daftar.map((c) => (
            <button
              key={c.customer_id}
              type="button"
              onClick={() => buka(c)}
              className={
                "w-full rounded-xl border p-3 text-left " +
                (pilih === c.customer_id
                  ? "border-primary bg-muted"
                  : "border-border bg-card hover:bg-muted")
              }
            >
              <p className="font-medium text-foreground">{c.name || "—"}</p>
              <p className="text-xs text-muted-foreground">
                {c.dr_number} · {formatDateTime(c.tanda_jadi_confirmed_at)}
              </p>
              {/* ⛔ Keadaan pertama dari tiga — ditandai DI DAFTAR, bukan cuma
                  saat dibuka. Customer yang nomornya nol terbaca nol akan
                  pernah menerima satu pun pesan, dan itu perlu terlihat
                  sebelum ada yang bertanya kenapa ia nol pernah membalas. */}
              {!c.nomor_terbaca && (
                <p className="mt-1 text-xs text-destructive">
                  Nomor tidak terbaca — pesan tidak akan pernah sampai
                </p>
              )}
            </button>
          ))}
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          {!pilih && (
            <p className="text-sm text-muted-foreground">
              Pilih customer di sebelah kiri.
            </p>
          )}
          {pilih && !tl && <p className="text-sm text-muted-foreground">Memuat…</p>}
          {tl && (
            <>
              <div className="mb-3 border-b border-border pb-2">
                <p className="font-medium text-foreground">{tl.customer?.name}</p>
                <p className="text-xs text-muted-foreground">{tl.customer?.phone}</p>
              </div>

              {/* ⛔ Keadaan pertama vs kedua — dibedakan dengan sengaja.
                  Menyatukannya jadi "belum ada percakapan" membuat nomor yang
                  salah ketik nol pernah ketahuan. */}
              {!tl.nomor_terbaca && (
                <div className="mb-3 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  Nomor customer ini tidak bisa dibaca sebagai nomor telepon.
                  Selama begitu, ia tidak akan pernah menerima pesan apa pun —
                  termasuk tautan Ruang Pesanan.
                </div>
              )}
              {tl.nomor_terbaca && !tl.nomor_cocok && (
                <div className="mb-3 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground">
                  Belum ada pesan WhatsApp untuk nomor ini — customer memang
                  belum pernah berkirim pesan.
                </div>
              )}

              <div className="space-y-1">
                {tl.baris.map((b) => <Baris key={`${b.arah}-${b.id || b.waktu}`} b={b} isOwner={isOwner} />)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
